import { TablePersistenceDirective } from '../../directive/table-persistence.directive';
import { CommonModule, Location } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputTextModule } from 'primeng/inputtext';
import { PanelModule } from 'primeng/panel';
import { Skeleton } from 'primeng/skeleton';
import { Table, TableModule } from 'primeng/table';
import { SortOrder } from '../../enum/sort-order.enum';
import { UserDirectoryUser } from '../../model/user/user-directory-user';
import { EmailStatusComponent } from '../email-status/email-status.component';
import { UserService } from '../../service/user.service';
import { IdentityService } from '../../service/identity.service';
import { environment } from '../../../environment/environment';
import { SalutationPipe } from '../../pipes/salutation.pipe';
import { CidaasUser } from '../../../feature/user-administration/model/cidaas-user';
import { Rs2UsersQuery } from '../../../feature/user-administration/model/rs2-users-query';
import { Constants } from '../../helper/Constants';
import { Role } from '../../../core/auth/role.enum';
import { clearTableSessionState, getTableSearchValue, getTableSessionState, persistTableSessionStatePatch, setTableSearchValue } from '../../helper/table-session-state';
import { catchError, debounceTime, distinctUntilChanged, EMPTY, finalize, Subject, switchMap, takeUntil } from 'rxjs';
import { canRunTableSearch, PrimeNgTableSearch } from '../../helper/primeng-table-search';

@Component({
  selector: 'gate-user-directory',
  standalone: true,
  imports: [
    TablePersistenceDirective,
    CommonModule,
    PanelModule,
    TranslatePipe,
    Skeleton,
    TableModule,
    ButtonModule,
    FormsModule,
    InputTextModule,
    InputGroupModule,
    EmailStatusComponent,
    SalutationPipe,
  ],
  templateUrl: './user-directory.component.html',
  styleUrl: './user-directory.component.scss',
})
export class UserDirectoryComponent implements OnInit, OnDestroy {
  private readonly tableStateKey = 'userDirectoryTableState';
  private readonly searchDebounceMs = PrimeNgTableSearch.debounceMs;
  private userService = inject(UserService);
  private identityService = inject(IdentityService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private locationService = inject(Location);

  public users = signal<UserDirectoryUser[]>([]);
  public isLoading = signal<boolean>(false);
  public searchFilter = signal<string>('');
  public totalRecords = 0;
  public itemsPerPage = environment.DefaultItemsPerPage;
  public rs2Query = new Rs2UsersQuery();
  public isModuleAdmin = this.userService.currentUser().roles.includes(Role.ModuleAdmin);

  private isRestoringTableState = false;
  private readonly searchInput$ = new Subject<string>();
  private readonly loadRequests$ = new Subject<{ query: Rs2UsersQuery | null; key: string }>();
  private readonly destroy$ = new Subject<void>();
  @ViewChild('userTable') userTable!: Table
  constructor() {
    if (!this.isModuleAdmin) {
      this.router.navigate([Constants.routes.home()]);
    }
  }
  ngOnInit(): void {
    this.setupSearchSubscription();
    this.setupLoadSubscription();

    const routeSearch = this.route.snapshot.queryParamMap.get('search');
    if (routeSearch != null) {
      clearTableSessionState(this.tableStateKey);
      this.searchFilter.set(routeSearch.trim());
    }

    this.configureInitialUserQuery();

    if (routeSearch == null) {
      this.searchFilter.set((getTableSearchValue(this.tableStateKey) ?? '').trim());
    }

    this.locationService.replaceState(Constants.routes.userAdministration());
    this.searchRs2Users(false);

    if (this.isRestoringTableState) {
      setTimeout(() => {
        this.isRestoringTableState = false;
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onSearchFilterChange(value: string): void {
    this.searchFilter.set(value);
    this.searchInput$.next(value);
  }

  public searchUser(): void {
    this.searchRs2Users();
  }

  public pageChange(event: any): void {
    if (this.isRestoringTableState) return;
    this.onRs2PageChange(event);
  }

  public onSort(event: any): void {
    if (this.isRestoringTableState) return;
    this.onRs2Sort(event);
  }

  public clearDataTable(table: Table): void {
    if (!this.isModuleAdmin) {
      this.router.navigate([Constants.routes.home()]);
    }
    table.clear();
    clearTableSessionState(this.tableStateKey);
    void this.clearRs2Table();
  }

  public openUser(user: UserDirectoryUser): void {
    this.router.navigate(
      [Constants.routes.userAdministrationPath, user.key],
      { state: { user } },
    );
  }

  private configureInitialUserQuery(): void {
    this.rs2Query.top = this.itemsPerPage;
    this.rs2Query.skip = 0;
    this.rs2Query.sortExpression = {
      fieldName: 'cidaasCreationDate',
      order: SortOrder.Descending,
    };
    this.rs2Query.thenSortExpression = undefined;
    const restoredState = getTableSessionState(this.tableStateKey);
    this.isRestoringTableState = !!restoredState;
    if (restoredState?.rows != null) {
      this.itemsPerPage = restoredState.rows;
      this.rs2Query.top = restoredState.rows;
    }
    if (restoredState?.first != null) {
      this.rs2Query.skip = restoredState.first;
    }
    if (restoredState?.sortField && restoredState.sortOrder != null) {
      const primaryField = this.mapRs2SortField(restoredState.sortField);
      const order = restoredState.sortOrder === 1 ? SortOrder.Ascending : SortOrder.Descending;
      this.rs2Query.sortExpression = { fieldName: primaryField, order };
      this.rs2Query.thenSortExpression = primaryField === 'firstName'
        ? { fieldName: 'lastName', order }
        : undefined;
    }
  }

  private setupSearchSubscription(): void {
    this.searchInput$
      .pipe(
        debounceTime(this.searchDebounceMs),
        distinctUntilChanged((previous, current) => previous.trim() === current.trim()),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.searchRs2Users());
  }

  private setupLoadSubscription(): void {
    this.loadRequests$
      .pipe(
        debounceTime(0),
        distinctUntilChanged((previous, current) => previous.key === current.key),
        switchMap(({ query }) => {
          if (!query) {
            this.isLoading.set(false);
            return EMPTY;
          }

          this.isLoading.set(true);
          return this.identityService.getCidaasUsers$(query).pipe(
            catchError((error) => {
              console.error('Error loading RS2 users:', error);
              return EMPTY;
            }),
            finalize(() => {
              this.persistTableStateFromQuery();
              this.isLoading.set(false);
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((result) => {
        this.users.set((result.cidaasUsers ?? []).map((item) => this.mapCidaasUser(item)));
        this.totalRecords = result.totalCount ?? 0;
      });
  }

  private searchRs2Users(resetPage = true): void {
    const search = this.searchFilter().trim();
    if (!canRunTableSearch(search)) {
      this.loadRequests$.next({ query: null, key: `invalid-search:${search}` });
      return;
    }
    this.rs2Query.searchString = search;
    setTableSearchValue(this.tableStateKey, search);
    if (resetPage) {
      this.rs2Query.skip = 0;
    }
    this.rs2Query.top = this.itemsPerPage;
    this.loadRs2Users();
  }

  private loadRs2Users(): void {
    const query = this.cloneRs2Query();
    this.loadRequests$.next({ query, key: JSON.stringify(query) });
  }

  private onRs2PageChange(event: any): void {
    this.itemsPerPage = event.rows;
    this.rs2Query.skip = event.first;
    this.rs2Query.top = event.rows;
    this.loadRs2Users();
  }

  private onRs2Sort(event: any): void {
    const primaryField = this.mapRs2SortField(event.field || '');
    const order = event.order === 1 ? SortOrder.Ascending : SortOrder.Descending;
    this.rs2Query.sortExpression = {
      fieldName: primaryField,
      order,
    };
    this.rs2Query.thenSortExpression = primaryField === 'firstName'
      ? { fieldName: 'lastName', order }
      : undefined;
    this.rs2Query.skip = 0;
    this.loadRs2Users();
  }

  private mapRs2SortField(field: string): string {
    if (field === 'email') {
      return 'eMail';
    }
    if (field === 'createdAt') {
      return 'cidaasCreationDate';
    }
    return field;
  }

  public getUserTableSortField(): string | undefined {
    const fieldName = this.rs2Query.sortExpression?.fieldName;

    if (fieldName === 'cidaasCreationDate') {
      return 'createdAt';
    }

    if (fieldName === 'eMail') {
      return 'email';
    }

    return fieldName;
  }

  private persistTableStateFromQuery(): void {
    persistTableSessionStatePatch(this.tableStateKey, {
      first: this.rs2Query.skip ?? 0,
      rows: this.rs2Query.top ?? this.itemsPerPage,
      sortField: this.getUserTableSortField(),
      sortOrder: this.rs2Query.sortExpression?.order ?? SortOrder.Descending,
    });
  }

  private async clearRs2Table(): Promise<void> {
    this.searchFilter.set('');
    this.users.set([]);
    this.totalRecords = 0;
    this.rs2Query = new Rs2UsersQuery();
    this.rs2Query.top = this.itemsPerPage;
    this.rs2Query.skip = 0;
    this.loadRs2Users();
  }

  private cloneRs2Query(): Rs2UsersQuery {
    return Object.assign(new Rs2UsersQuery(), {
      ...this.rs2Query,
      sortExpression: this.rs2Query.sortExpression ? { ...this.rs2Query.sortExpression } : undefined,
      thenSortExpression: this.rs2Query.thenSortExpression ? { ...this.rs2Query.thenSortExpression } : undefined,
    });
  }

  private mapCidaasUser(item: CidaasUser): UserDirectoryUser {
    return {
      key: item.cidaasSub || item.userId || item.eMail,
      firstName: item.firstName,
      lastName: item.lastName,
      salutation: item.salutation,
      email: item.eMail,
      emailVerified: item.cidaasEMailVerified,
      createdAt: item.cidaasCreationDate ? new Date(item.cidaasCreationDate) : undefined,
      payload: { cidaas: item },
    };
  }
}
